import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

export const A11yAnnouncer = () => {
    const location = useLocation()
    const [message, setMessage] = useState('')
    const [announceKey, setAnnounceKey] = useState(0) // Used to force re-announcement
    const prevMessageRef = useRef<string>('')
    const announcementRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()

    // This effect handles detecting page title changes
    useEffect(() => {
        const observer = new MutationObserver(() => {
            const h1 = document.querySelector('h1')
            if (h1?.textContent) {
                const text = h1.textContent.trim()
                if (text && text !== prevMessageRef.current) {
                    prevMessageRef.current = text
                    setMessage(text)
                    // Force a re-announcement by changing the key
                    setAnnounceKey((prev) => prev + 1)
                }
            }
        })

        const timeout = setTimeout(() => {
            const main = document.querySelector('#root')
            if (main) {
                observer.observe(main, { childList: true, subtree: true })
                // Also check once in case it's already rendered
                const h1 = main.querySelector('h1')
                if (h1?.textContent) {
                    const text = h1.textContent.trim()
                    if (text && text !== prevMessageRef.current) {
                        prevMessageRef.current = text
                        setMessage(text)
                        // Force a re-announcement by changing the key
                        setAnnounceKey((prev) => prev + 1)
                    }
                }
            }
        }, 100) // slight delay to wait for new page mount

        return () => {
            clearTimeout(timeout)
            observer.disconnect()
        }
    }, [location])

    // This effect handles ensuring the announcement is fully read
    useEffect(() => {
        if (!message || !announcementRef.current) return

        // Clear and then set the content with a small delay
        // This technique forces screen readers to treat it as a new announcement
        const timeoutId = setTimeout(() => {
            if (announcementRef.current) {
                // Clear the element
                announcementRef.current.textContent = ''

                // After a brief pause, add the content back
                const restoreTimeoutId = setTimeout(() => {
                    if (announcementRef.current) {
                        // Set the content and add a period at the end to force a pause
                        const announcement = t(
                            'a11y.announcer',
                            `The page updated. You are now on a page titled: ${message}`,
                        )
                        announcementRef.current.textContent = `${announcement}. `
                    }
                }, 50)

                return () => clearTimeout(restoreTimeoutId)
            }
        }, 20)

        return () => clearTimeout(timeoutId)
    }, [message, announceKey, t])

    // Using two separate ARIA live regions with different politeness levels
    // increases the chance that screen readers will prioritize the announcement
    return (
        <>
            {/* Primary announcer - assertive for high priority */}
            <div
                ref={announcementRef}
                aria-live="assertive"
                aria-atomic="true"
                role="alert"
                className="sr-only"
                data-testid="a11y-announcer"
            >
                {/* Intentionally empty, content set via useEffect */}
            </div>

            {/* Backup announcer - polite as fallback */}
            <div aria-live="polite" aria-atomic="true" role="status" className="sr-only">
                {message
                    ? t(
                          'a11y.announcer',
                          `The page updated. You are now on a page titled: ${message}`,
                      )
                    : ''}
            </div>
        </>
    )
}
